import { useState, useCallback, useEffect } from 'react';

import { FileIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import { DOCUMENT_TYPES_WITH_PREVIEW } from '@/modules/collections/constants';
import type { Document } from '@/shared/types/collections';

/**
 * DocumentPreview component for rendering different document types
 * Uses Google Docs Viewer for PDF and DOCX files
 * Direct text display for TXT and MD files
 * @param url - Document URL
 * @param document - Document metadata (can be undefined while loading)
 */
interface DocumentPreviewProps {
  url?: string;
  document?: Document;
}

/**
 * Loading skeleton for all document types
 */
const DocumentSkeleton = () => (
  <div className="flex h-full w-full flex-col">
    <div className="mb-4 flex items-center gap-2 border-b pb-2">
      <Skeleton className="h-5 w-5 bg-gray-300" />
      <Skeleton className="h-6 w-48 bg-gray-300" />
    </div>
    <div
      className="flex-1 rounded-lg border bg-gray-50 p-4"
      style={{ minHeight: '400px', maxHeight: '600px' }}
    >
      <div className="space-y-3">
        {/* Simulate text lines */}
        <Skeleton className="h-4 w-full bg-gray-300" />
        <Skeleton className="h-4 w-5/6 bg-gray-300" />
        <Skeleton className="h-4 w-4/5 bg-gray-300" />
        <Skeleton className="h-4 w-full bg-gray-300" />
        <Skeleton className="h-4 w-3/4 bg-gray-300" />
        <Skeleton className="h-4 w-5/6 bg-gray-300" />
        <Skeleton className="h-4 w-2/3 bg-gray-300" />
        <Skeleton className="h-4 w-full bg-gray-300" />
        <Skeleton className="h-4 w-4/5 bg-gray-300" />
        <Skeleton className="h-4 w-3/5 bg-gray-300" />
        <Skeleton className="h-4 w-5/6 bg-gray-300" />
        <Skeleton className="h-4 w-full bg-gray-300" />
        <Skeleton className="h-4 w-2/3 bg-gray-300" />
        <Skeleton className="h-4 w-4/5 bg-gray-300" />
        <Skeleton className="h-4 w-3/4 bg-gray-300" />
      </div>
    </div>
  </div>
);

/**
 * DocumentPreview component for rendering different document types
 * Uses Google Docs Viewer for PDF and DOCX files
 * Direct text display for TXT and MD files
 * @param url - Document URL
 * @param document - Document metadata
 */
export const DocumentPreview = ({ url, document }: DocumentPreviewProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string>('');

  /**
   * Determines if the document type is supported for rendering
   */
  const getDocumentType = useCallback(() => {
    const fileType = document?.type?.toLowerCase() || '';
    const fileName = document?.name?.toLowerCase() || '';

    if (fileType.includes('pdf')) {
      return 'pdf';
    }
    if (fileType.includes('doc')) {
      return 'docx';
    }
    if (fileType.includes('txt')) {
      return 'txt';
    }
    if (
      fileType.includes('text') ||
      fileName.endsWith('.md') ||
      fileName.endsWith('.log') ||
      fileName.endsWith('.csv') ||
      fileType.includes('markdown')
    ) {
      return 'txt';
    }
    return 'unsupported';
  }, [document?.type, document?.name]);

  /**
   * Fetches text content from URL
   */
  const fetchTextContent = useCallback(
    async (textUrl: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(textUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const content = await response.text();
        // Remove any existing <br> tags that might be in the text content
        const cleanContent = content.replace(/<br\s*\/?>/gi, '');
        setTextContent(cleanContent);
        setLoading(false);
      } catch {
        setError(t('collections.documents.errors.failed_to_load'));
        setLoading(false);
      }
    },

    [t]
  );

  /**
   * Effect to fetch text content when URL changes and document is txt
   */
  useEffect(() => {
    if (url && document && getDocumentType() === 'txt') {
      fetchTextContent(url);
    }
  }, [url, document, getDocumentType, fetchTextContent]);

  /**
   * Handles iframe load success
   */
  const handleIframeLoad = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  /**
   * Handles iframe load error
   */
  const handleIframeError = useCallback(() => {
    setError(t('collections.documents.errors.failed_to_load'));
    setLoading(false);
  }, [t]);

  // Handle document loading state
  if (!document || !url) {
    return <DocumentSkeleton />;
  }

  const documentType = getDocumentType();
  const isSupported = DOCUMENT_TYPES_WITH_PREVIEW.includes(documentType);

  /**
   * Renders text file content
   */
  const renderTextViewer = () => {
    if (loading) {
      return <DocumentSkeleton />;
    }

    return (
      <div className="flex h-full w-full flex-col">
        <div className="mb-4 flex items-center gap-2 border-b pb-2">
          <FileTextIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-medium text-gray-800">
            {document?.name || t('common.preview')}
          </h3>
        </div>
        <div
          className="flex-1 overflow-auto rounded-lg border bg-gray-50 p-4"
          style={{ minHeight: '400px', maxHeight: '600px' }}
        >
          <pre className="font-mono text-sm whitespace-pre-wrap text-gray-800">{textContent}</pre>
        </div>
      </div>
    );
  };

  /**
   * Renders document viewer using Google Docs Viewer
   */
  const renderDocumentViewer = () => {
    const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(url)}&embedded=true`;

    return (
      <div className="relative h-full w-full">
        <iframe
          src={viewerUrl}
          width="100%"
          height="100%"
          style={{ minHeight: '600px' }}
          className="border-0"
          title={document?.name || t('common.preview')}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
        />
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background">
            <DocumentSkeleton />
          </div>
        )}
      </div>
    );
  };

  /**
   * Renders error state
   */
  const renderError = () => (
    <div className="flex h-64 flex-col items-center justify-center text-gray-500">
      <AlertCircleIcon className="mb-4 h-12 w-12 text-red-400" />
      <h3 className="mb-2 text-lg font-medium">{t('collections.documents.errors.title')}</h3>
      <p className="mb-4 max-w-md text-center">
        {error || t('collections.documents.errors.generic_document')}
      </p>
    </div>
  );

  /**
   * Renders unsupported document type
   */
  const renderUnsupported = () => (
    <div className="flex h-64 flex-col items-center justify-center text-gray-500">
      <FileIcon className="mb-4 h-12 w-12" />
      <h3 className="mb-2 text-lg font-medium">
        {t('collections.documents.errors.unsupported_document')}
      </h3>
      <p className="mb-4 max-w-md text-center">
        {t('collections.documents.errors.unsupported_document_description')}
      </p>
    </div>
  );

  // Handle error state
  if (error) {
    return renderError();
  }

  // Handle different document types
  if (isSupported) {
    if (documentType === 'txt') {
      return renderTextViewer();
    } else {
      return renderDocumentViewer();
    }
  } else {
    return renderUnsupported();
  }
};
